import { countryCodes } from "./countryCodes";

const FALLBACK_CURRENCY_CODES = `
AED AFN ALL AMD ANG AOA ARS AUD AWG AZN BAM BBD BDT BGN BHD BIF BMD BND BOB BOV BRL BSD BTN BWP BYN BZD CAD CDF CHE CHF CHW CLF CLP CNY COP COU CRC CUP CVE CZK DJF DKK DOP DZD EGP ERN ETB EUR FJD FKP GBP GEL GHS GIP GMD GNF GTQ GYD HKD HNL HTG HUF IDR ILS INR IQD IRR ISK JMD JOD JPY KES KGS KHR KMF KPW KRW KWD KYD KZT LAK LBP LKR LRD LSL LYD MAD MDL MGA MKD MMK MNT MOP MRU MUR MVR MWK MXN MXV MYR MZN NAD NGN NIO NOK NPR NZD OMR PAB PEN PGK PHP PKR PLN PYG QAR RON RSD RUB RWF SAR SBD SCR SDG SEK SGD SHP SLE SLL SOS SRD SSP STN SVC SYP SZL THB TJS TMT TND TOP TRY TTD TWD TZS UAH UGX USD UYI UYU UZS VES VND VUV WST XAF XCD XOF XPF YER ZAR ZMW ZWL
`.trim().split(/\s+/);

const COUNTRY_CODE_SET = new Set(countryCodes.map((country) => country.code));
const getCountryName = (code) => {
  const knownCountry = countryCodes.find((country) => country.code === code);
  if (knownCountry) return knownCountry.name;
  try {
    return new Intl.DisplayNames(["en"], { type: "region" }).of(code) || code;
  } catch {
    return code;
  }
};
const CURRENCY_COUNTRY_CODE_OVERRIDES = {
  EUR: "EU",
  XAF: "CM",
  XCD: "AG",
  XDR: "UN",
  XOF: "SN",
  XPF: "PF",
};
const CURRENCY_SYMBOL_LOCALES = {
  ALL: "sq-AL",
  ANG: "nl-CW",
  AWG: "nl-AW",
  BGN: "bg-BG",
  BHD: "ar-BH",
  BIF: "fr-BI",
  BYN: "be-BY",
  CDF: "fr-CD",
  CVE: "pt-CV",
  DJF: "fr-DJ",
  DZD: "ar-DZ",
  ERN: "en-ER",
  ETB: "am-ET",
  GMD: "en-GM",
  HTG: "fr-HT",
  IQD: "ar-IQ",
  IRR: "fa-IR",
  JOD: "ar-JO",
  KES: "en-KE",
  KWD: "ar-KW",
  LSL: "en-LS",
  LYD: "ar-LY",
  MAD: "ar-MA",
  MDL: "ro-MD",
  MKD: "mk-MK",
  MOP: "en-MO",
  MRU: "ar-MR",
  MVR: "en-MV",
  MWK: "en-MW",
  MZN: "pt-MZ",
  OMR: "ar-OM",
  PAB: "es-PA",
  PEN: "es-PE",
  PGK: "en-PG",
  QAR: "ar-QA",
  RSD: "sr-RS",
  SAR: "ar-SA",
  SCR: "en-SC",
  SDG: "ar-SD",
  SLE: "en-SL",
  SLL: "en-SL",
  SOS: "so-SO",
  SVC: "es-SV",
  SZL: "en-SZ",
  TJS: "tg-TJ",
  TMT: "tk-TM",
  TND: "ar-TN",
  TZS: "en-TZ",
  UGX: "en-UG",
  UZS: "uz-UZ",
  VES: "es-VE",
  VUV: "en-VU",
  WST: "en-WS",
  XAF: "fr-CM",
  XCD: "en-AG",
  XOF: "fr-SN",
  XPF: "fr-PF",
  YER: "ar-YE",
};
const CURRENCY_SYMBOL_FALLBACKS = {
  ALL: "Lek" + String.fromCharCode(0x00eb),
  ANG: String.fromCharCode(0x0192),
  AWG: String.fromCharCode(0x0192),
  BGN: String.fromCharCode(0x043b, 0x0432) + ".",
  CHF: "CHF",
  CVE: "Esc",
  ETB: "Br",
  LSL: "L",
  MDL: "L",
  MZN: "MT",
  SLL: "Le",
  SVC: String.fromCharCode(0x20a1),
  TJS: "SM",
  TMT: "m",
  UZS: "so" + String.fromCharCode(0x02bb) + "m",
  XDR: "SDR",
  XSU: "XSU",
  ZWG: "ZiG",
  ZWL: "Z$",
};

const getCurrencyOptions = () => {
  let codes = [];
  try {
    if (typeof Intl.supportedValuesOf === "function") {
      codes = Intl.supportedValuesOf("currency");
    }
  } catch {
    codes = [];
  }

  if (!codes.length) codes = FALLBACK_CURRENCY_CODES;
  if (!codes.includes("USD")) codes = [...codes, "USD"];

  let names;
  try {
    names = new Intl.DisplayNames(["en"], { type: "currency" });
  } catch {
    names = null;
  }

  return [...new Set(codes)]
    .map((code) => {
      const prefix = code.slice(0, 2);
      const countryCode =
        CURRENCY_COUNTRY_CODE_OVERRIDES[code] ||
        (COUNTRY_CODE_SET.has(prefix) ? prefix : code);
      const locales = [
        "en-US",
        CURRENCY_SYMBOL_LOCALES[code],
        "en-" + countryCode,
      ].filter(Boolean);
      let symbol = code;

      for (const locale of locales) {
        try {
          const formattedSymbol = new Intl.NumberFormat(locale, {
            style: "currency",
            currency: code,
            currencyDisplay: "narrowSymbol",
          }).formatToParts(0).find((part) => part.type === "currency")?.value;
          if (formattedSymbol && formattedSymbol !== code) {
            symbol = formattedSymbol;
            break;
          }
        } catch {
          // Try the next locale for this currency.
        }
      }

      if (symbol === code) {
        symbol = CURRENCY_SYMBOL_FALLBACKS[code] || code;
      }

      return {
        code,
        countryCode,
        countryName: getCountryName(countryCode),
        name: names?.of(code) || code,
        symbol,
      };
    })
    .sort((first, second) => first.name.localeCompare(second.name));
};

export const CURRENCY_OPTIONS = getCurrencyOptions();
export const CURRENCY_DROPDOWN_OPTIONS = CURRENCY_OPTIONS.map((currency) => ({
  value: currency.code,
  code: currency.code,
  countryCode: currency.countryCode,
  symbol: currency.symbol,
  displayCountry: true,
  selectedLabel: currency.code + " \u2014 " + currency.symbol,
  label: currency.countryName + " \u2014 " + currency.symbol,
  description: currency.name,
  searchText: [currency.countryName, currency.countryCode, currency.code, currency.symbol, currency.name].join(" "),
}));
export const QUANTITY_UNIT_OPTIONS = [
  ["kg", "Kilogram"],
  ["g", "Gram"],
  ["ton", "Metric ton"],
  ["MT", "Metric ton (MT)"],
  ["tonne", "Tonne"],
  ["t", "Tonne (t)"],
  ["mg", "Milligram"],
  ["barrel", "Barrel"],
  ["m3", "Cubic metre"],
  ["litre", "Litre"],
  ["liter", "Liter"],
  ["gal", "Gallon"],
  ["lb", "Pound"],
  ["oz", "Ounce"],
  ["unit", "Unit"],
  ["piece", "Piece"],
  ["pcs", "Pieces"],
  ["box", "Box"],
  ["pack", "Pack"],
  ["set", "Set"],
  ["pair", "Pair"],
  ["dozen", "Dozen"],
  ["L", "Litre"],
  ["ml", "Millilitre"],
  ["m", "Metre"],
  ["cm", "Centimetre"],
  ["mm", "Millimetre"],
  ["km", "Kilometre"],
  ["ft", "Foot"],
  ["in", "Inch"],
].map(([value, description]) => ({
  value,
  label: value,
  description,
  searchText: value + " " + description,
}));

export const getCurrencyCode = (value) => {
  const currencyValue = String(value || "USD").trim();
  if (CURRENCY_OPTIONS.some((currency) => currency.code === currencyValue)) {
    return currencyValue;
  }
  if (currencyValue === "$") return "USD";
  return CURRENCY_OPTIONS.find((currency) => currency.symbol === currencyValue)?.code || "USD";
};

